import { DataTable, TableMenu } from '@/components'
import type { TableMenuItem } from '@/components'
import { cn } from '@/lib/utils'
import type { VehicleExpirationNode } from '@/modules/vehicles'
import {
  EXPIRATION_TYPES,
  getVehicleExpirationStatus,
} from '@/modules/vehicles'
import { PERMISSIONS, usePermissions } from '@/utils'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { ExpirationStatusBadge } from '@/modules/vehicles/list-filters'
import { getVehicleExpirationTypeName } from '../util'

interface Props {
  expirations: VehicleExpirationNode[]
  loading?: boolean
  onEdit(expiration: VehicleExpirationNode): void
  onDelete(expiration: VehicleExpirationNode): void
  onMoveToServices(expiration: VehicleExpirationNode): void
}

export const VehicleExpirationsTable: React.FC<Props> = ({
  expirations,
  loading = false,
  onEdit,
  onDelete,
  onMoveToServices,
}) => {
  const translations = useTranslate({
    vehicle: 'common.vehicle',
    type: 'common.type',
    description: 'common.description',
    expirationDate: 'documents.expiration-date',
    status: 'common.status',
    notificationTime: 'common.notification',
    responsibleUser: 'common.responsible-user',
    noExpirationDates: 'vehicles.no-expiration-dates',
    edit: 'common.edit',
    delete: 'common.delete',
    moveToServices: 'vehicles.expirations.move-to-services',
    items: ['vehicles.expirations.num-expiration-dates', { n: 0 }],
  })

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  const column = createColumnHelper<VehicleExpirationNode>()

  const columns = useMemo(
    () => [
      column.accessor('vehicle', {
        header: translations.vehicle,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.vehicle?.registrationNumber ?? '').localeCompare(
            b.original.vehicle?.registrationNumber ?? ''
          ),
        cell(info) {
          const vehicle = info.getValue()
          if (!vehicle) return '-'

          return (
            <Link className="text-inherit" to={`/vehicle-fleet/${vehicle.id}`}>
              {vehicle.registrationNumber || '-'}
            </Link>
          )
        },
      }),
      column.accessor('expirationType', {
        header: translations.type,
        enableSorting: true,
        sortingFn: (a, b) =>
          getVehicleExpirationTypeName({
            expiration: a.original,
          }).localeCompare(
            getVehicleExpirationTypeName({ expiration: b.original })
          ),
        cell: info =>
          getVehicleExpirationTypeName({ expiration: info.row.original }),
      }),
      column.accessor('description', {
        header: translations.description,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('expirationDate', {
        header: translations.expirationDate,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.expirationDate).getTime() -
          new Date(b.original.expirationDate).getTime(),
        cell(info) {
          const expirationDate = info.getValue()
          if (!expirationDate) return '-'
          return format(new Date(expirationDate), 'PP')
        },
      }),
      column.display({
        id: 'status',
        header: translations.status,
        meta: { narrow: true },
        cell(info) {
          const status = getVehicleExpirationStatus(
            info.row.original.expirationDate
          )
          return <ExpirationStatusBadge status={status} />
        },
      }),
      column.accessor('notificationTime', {
        header: translations.notificationTime,
        meta: { narrow: true },
        cell(info) {
          const { expirationDate } = info.row.original
          const notificationTime = info.getValue()
          if (!notificationTime || !expirationDate) return '-'

          return formatDistanceStrict(
            new Date(expirationDate),
            new Date(notificationTime)
          )
        },
      }),
      column.accessor('responsibleUser', {
        header: translations.responsibleUser,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.responsibleUser?.fullName ?? '').localeCompare(
            b.original.responsibleUser?.fullName ?? ''
          ),
        cell: info => info.getValue()?.fullName || '-',
      }),
      column.display({
        id: 'menu',
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const expiration = info.row.original
          // The EU control date is derived from vegvesen data, so it must not
          // be deleted here — same rule as the vehicle detail page.
          const isEuControl =
            getVehicleExpirationTypeName({ expiration }) ===
            EXPIRATION_TYPES.nextEuControl

          const items: TableMenuItem[] = []
          if (canEdit) {
            items.push({
              id: 'edit',
              type: 'regular',
              label: translations.edit,
              icon: 'edit',
              onClick: () => onEdit(expiration),
            })
          }
          // Moving a row creates one and deletes another, so it takes both
          // rights. The EU control date is owned by Vegvesen, which would
          // recreate the expiration on the next vehicle refresh.
          if (canAdd && canDelete && !isEuControl) {
            items.push({
              id: 'move-to-services',
              type: 'regular',
              label: translations.moveToServices,
              icon: 'wrench',
              onClick: () => onMoveToServices(expiration),
            })
          }
          if (canDelete && !isEuControl) {
            items.push({
              id: 'delete',
              type: 'regular',
              label: translations.delete,
              icon: 'trash',
              onClick: () => onDelete(expiration),
            })
          }
          if (items.length === 0) return null

          return <TableMenu items={items} />
        },
      }),
    ],
    [
      canAdd,
      canDelete,
      canEdit,
      column,
      onDelete,
      onEdit,
      onMoveToServices,
      translations.delete,
      translations.description,
      translations.edit,
      translations.expirationDate,
      translations.moveToServices,
      translations.notificationTime,
      translations.responsibleUser,
      translations.status,
      translations.type,
      translations.vehicle,
    ]
  )

  return (
    <DataTable
      className={cn('w-full')}
      columns={columns}
      data={expirations}
      loading={loading}
      inCard
      initialSort={[{ id: 'expirationDate', desc: false }]}
      noDataText={translations.noExpirationDates}
      pagination={{ itemsText: n => translations.items({ n }) }}
    />
  )
}
